import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  invalid?: boolean;
}

/**
 * Styled text input matching the TailAdmin form controls. The `leftIcon` /
 * `rightIcon` slots render visual adornments inside the input frame and do
 * not interfere with the underlying native element.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leftIcon, rightIcon, invalid, className, disabled, ...rest },
  ref,
) {
  return (
    <div
      className={cn(
        'relative flex h-10 items-center rounded-lg border bg-white text-sm text-gray-900 shadow-xs transition-colors',
        'focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-200',
        'dark:bg-gray-900 dark:text-white dark:focus-within:ring-brand-900/60',
        invalid
          ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-200 dark:border-red-500'
          : 'border-gray-300 dark:border-gray-700',
        disabled && 'bg-gray-50 opacity-70 dark:bg-gray-800',
        className,
      )}
    >
      {leftIcon && (
        <span className="flex items-center pl-3 text-gray-400 dark:text-gray-500">
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        disabled={disabled}
        className={cn(
          'h-full min-w-0 flex-1 bg-transparent px-3 text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500',
          leftIcon && 'pl-2',
          rightIcon && 'pr-2',
        )}
        {...rest}
      />
      {rightIcon && (
        <span className="flex items-center pr-3 text-gray-400 dark:text-gray-500">
          {rightIcon}
        </span>
      )}
    </div>
  );
});
